import { format, hasText } from "utils/helpers"
import JumpingLama from "../components/jumping-lama"
import LessonBackground from "../components/lesson-background"
import { useMessages } from "../language/language"
import { LoadingReason, setGlobalDontPreventBrowserClose } from "../play-model"
import "./loading-frame.less"
import { useEffect, useMemo, useRef } from "react"
import If from "utils/components/if"
import { usePlayState } from "play/hooks/use-play-state"
import {
  applyTimerToState,
  connectionClosedReducer,
  switchToNextScreenReducer,
} from "play/reducers"
import { serverPlayExit } from "utils/server"

interface Props {
  reason?: LoadingReason
}

export default function LoadingFrame(props: Props) {
  const msg = useMessages()
  const {
    loadingReason,
    feedbackDurationInSeconds,
    className,
    imagesToPreload,
    fullName,
    isTeacherUrl,
    isBusinessVersion,
    theme,
    connectionId,
    modifyState,
  } = usePlayState()
  const loadedImages = useRef<number>(0)
  const timerId = useRef<ReturnType<typeof setTimeout> | null>(null)

  function loadingImagesCompleted() {
    modifyState((state) => {
      state.loadingReason = null
      state.internalPage = null

      const nextTimer = state.nextTimer

      console.log("loadingImagesCompleted: nextTimer", nextTimer)

      state.nextTimer = null
      if (!nextTimer) {
        console.log("state.next is empty!")
      } else {
        applyTimerToState(state, nextTimer)
      }
    })
  }

  async function studentExitGame() {
    try {
      await serverPlayExit(connectionId || "<empty>")
      console.log("Student removed from server session, cleaning up")
    } catch (e) {
      console.log("Student failed to be removed from server, clean up anyway")
    }

    setGlobalDontPreventBrowserClose(true)
    modifyState((state) => {
      connectionClosedReducer(state, { logout: true })
    })
  }

  const text = useMemo(() => {
    switch (loadingReason) {
      case "ClassroomLobby":
        return msg.studentWaitPage_lobby
      case "OtherStudentsAnswering":
        return msg.studentWaitPage_waitForAnswers
      case "TeacherWaitsForStudentFeedback":
        return msg.studentWaitPage_teacherWaitForStudents
      case "LoadingImages":
        return format(
          msg.loadingImageFormat,
          loadedImages.current,
          imagesToPreload.length
        )
      case "Loading":
        return msg.studentWaitPage_generic
      default:
        return msg.studentWaitPage_generic
    }
  }, [msg, loadingReason, imagesToPreload.length, loadedImages.current])

  useEffect(() => {
    if (loadingReason === "TeacherWaitsForStudentFeedback") {
      if (timerId.current) {
        clearTimeout(timerId.current)
        timerId.current = null
      }

      if (feedbackDurationInSeconds > 0) {
        timerId.current = setTimeout(() => {
          modifyState((state) => {
            state.loadingReason = null
            state.internalPage = null
            state.feedbackDurationInSeconds = 0

            if (state.screenFinishedResponse) {
              const resp = state.screenFinishedResponse
              state.screenFinishedResponse = null
              applyTimerToState(state, resp)
              switchToNextScreenReducer(state, resp)
            }
          })
        }, feedbackDurationInSeconds * 1000)
      }
    } else if (loadingReason === "LoadingImages") {
      loadedImages.current = 0

      for (let url of imagesToPreload) {
        if (!hasText(url)) {
          loadedImages.current++
          if (loadedImages.current === imagesToPreload.length) {
            loadingImagesCompleted()
          }
          break
        }

        const img = new Image()
        img.onload = () => {
          loadedImages.current++
          if (loadedImages.current === imagesToPreload.length) {
            // console.log('LoadingPage: loadingImagesCompleted')
            loadingImagesCompleted()
          }
        }
        img.onerror = () => {
          loadedImages.current++
          if (loadedImages.current === imagesToPreload.length) {
            // console.log('LoadingPage: loadingImagesCompleted')
            loadingImagesCompleted()
          }
        }

        img.src = url
      }

      if (imagesToPreload.length === 0) {
        // console.log('LoadingPage: 0 images, loadingImagesCompleted')
        loadingImagesCompleted()
      }
    }

    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current)
        timerId.current = null
      }
    }
  }, [loadingReason, imagesToPreload, feedbackDurationInSeconds])

  console.log("reason", loadingReason, "className", className, "text", text)

  const showQuitButton =
    loadingReason === "ClassroomLobby" && hasText(className)

  const useNameAndClass =
    loadingReason === "ClassroomLobby" && !isTeacherUrl && hasText(fullName)

  return (
    <div className="loading-frame">
      <div className="foreground">
        <div className="text bg-classbuddy p-2 rounded">
          <If condition={useNameAndClass}>
            <div className="fs-2">
              <strong>{fullName}</strong>
            </div>
          </If>
          <If condition={showQuitButton}>
            <a
              href="#"
              className="text-white"
              onClick={(e) => {
                e.preventDefault()
                studentExitGame()
              }}
            >
              {msg.studentScreenFrame_quitGame}
            </a>
          </If>
          {useNameAndClass && hasText(className) && (
            <div className="fs-5 mt-2 mb-4">
              <em>{className}</em>
            </div>
          )}
          <h1>{text}</h1>
        </div>
        <JumpingLama className="loading-lama" />
      </div>
      <LessonBackground isBusinessVersion={isBusinessVersion} theme={theme} />
    </div>
  )
}
